import { useEffect, useMemo, useState } from "react";
import { Search, X, Star, ChefHat } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import TopBar from "../components/TopBar";
import CategoryBar from "../components/CategoryBar";
import MenuCard from "../components/MenuCard";
import CombosShowcase from "../components/CombosShowcase";
import CartSheet from "../components/CartSheet";
import StickyCartBar from "../components/StickyCartBar";
import SiteFooter from "../components/SiteFooter";
import SpinAndWin from "../components/spin/SpinAndWin";

const COMBO_CAT = "MLOBOX Signature Combos";
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

function Grid({ items, extras, testid, label }) {
  return (
    <div data-testid={testid} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
      {items.map((item) => <MenuCard key={item.id} item={item} extras={extras} unavailableLabel={label} />)}
    </div>
  );
}

function Section({ title, icon, children, testid }) {
  return (
    <section data-testid={testid} className="pt-8 first:pt-2">
      <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[#111111] mb-4 flex items-center gap-2">{icon}{title}</h2>
      {children}
    </section>
  );
}

export default function CustomerApp() {
  const [menu, setMenu] = useState([]);
  const [extras, setExtras] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const { refreshPrices } = useCart();

  useEffect(() => { refreshPrices().catch(() => {}); }, [refreshPrices]);

  useEffect(() => {
    Promise.all([api.get("/menu"), api.get("/extras"), api.get("/categories"), api.get("/menu-status")])
      .then(([m, e, c, s]) => { setMenu(m.data); setExtras(e.data); setCategories(c.data); setStatus(s.data); })
      .finally(() => setLoading(false));
  }, []);
  const label = status?.unavailable_label;

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => menu.filter((i) =>
    (category === "All" || i.category === category) &&
    (!q || `${i.name} ${i.description} ${i.category}`.toLowerCase().includes(q))
  ), [menu, category, q]);

  const browsing = category === "All" && !q;
  const combos = menu.filter((i) => i.category === COMBO_CAT);
  const highlights = menu.filter((i) => (i.special || i.featured) && i.category !== COMBO_CAT);
  const visibleCats = categories.filter((c) => menu.some((i) => i.category === c));

  return (
    <div className="min-h-screen pb-28 bg-[#FBF6EE]">
      <TopBar onOpenCart={() => setCartOpen(true)} />

      <section className="max-w-6xl mx-auto px-4 pt-5 sm:pt-8 pb-2">
        <div className="relative rounded-3xl overflow-hidden mlobox-gradient p-5 sm:p-8 text-white shadow-xl shadow-orange-200/50">
          <div className="relative z-10 max-w-xl">
            <h1 data-testid="hero-title" className="font-display text-3xl sm:text-5xl font-extrabold leading-tight">TASTE THE COMFORT OF HOME</h1>
            <p data-testid="hero-tagline" className="text-white/90 mt-3 text-sm sm:text-base font-semibold tracking-wide max-w-md">A GREAT MEAL AT YOUR DOOR STEP</p>
          </div>
          <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-white/10 blur-2xl"></div>
          <div className="absolute -right-20 -top-10 w-72 h-72 rounded-full bg-white/5 blur-3xl"></div>
        </div>
        <div className="relative mt-4">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="menu-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search meals, e.g. pilau, tilapia, samosa"
            className="w-full rounded-full bg-white border border-[#EFE7DA] pl-11 pr-10 py-3 text-sm outline-none focus:border-[#FF6600] focus:ring-2 focus:ring-orange-100" />
          {query && <button data-testid="menu-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400"><X size={16} /></button>}
        </div>
      </section>

      {status?.show_banner && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div data-testid="today-banner" className="rounded-2xl bg-[#111111] text-white px-5 py-4 flex items-center gap-3 border-l-4 border-[#FF6600]">
            <ChefHat size={22} className="text-[#FF8A3D] shrink-0" />
            <span className="font-semibold text-sm sm:text-base">{status.banner_text}</span>
          </div>
        </div>
      )}

      {browsing && !loading && <CombosShowcase combos={combos} unavailableLabel={label} />}
      {browsing && !loading && <SpinAndWin />}

      <CategoryBar categories={visibleCats} active={category} onChange={setCategory} />

      <main className="max-w-6xl mx-auto px-4 pt-3">
        {loading && <div className="text-center text-gray-500 py-16">Loading the menu…</div>}
        {!loading && menu.length === 0 && (
          <div data-testid="menu-empty" className="text-center text-gray-500 py-16">{status?.banner_text}</div>
        )}
        {!loading && browsing && (
          <>
            {highlights.length > 0 && (
              <Section testid="section-highlights" title="Featured & Today's Specials" icon={<Star size={20} className="text-[#FF6600]" />}>
                <Grid items={highlights} extras={extras} label={label} testid="highlights-grid" />
              </Section>
            )}
            {visibleCats.filter((c) => c !== COMBO_CAT).map((c) => (
              <Section key={c} title={c} testid={`section-${slug(c)}`}>
                <Grid items={menu.filter((i) => i.category === c)} extras={extras} label={label} />
              </Section>
            ))}
          </>
        )}
        {!loading && !browsing && (
          <div className="pt-2">
            <div className="text-sm text-gray-500 mb-3" data-testid="results-count">{filtered.length} result{filtered.length === 1 ? "" : "s"}</div>
            <Grid items={filtered} extras={extras} label={label} testid="menu-grid" />
            {filtered.length === 0 && <div className="text-center text-gray-500 py-12">No meals match your search.</div>}
          </div>
        )}
      </main>

      <SiteFooter />

      <StickyCartBar onOpen={() => setCartOpen(true)} />
      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
    </div>
  );
}
